"use client";

import { cva, VariantProps } from "class-variance-authority";
import { subDays, startOfDay, endOfDay, subYears } from "date-fns";
import { toDate, formatInTimeZone } from "date-fns-tz";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const multiSelectVariants = cva(
    "flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium text-admin-foreground ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default:
                    "bg-admin-primary text-admin-primary-foreground hover:bg-admin-primary/90",
                destructive:
                    "bg-admin-destructive text-admin-destructive-foreground hover:bg-admin-destructive/90",
                outline:
                    "border border-input bg-background hover:bg-admin-accent hover:text-admin-accent-foreground",
                secondary:
                    "bg-admin-secondary text-admin-secondary-foreground hover:bg-admin-secondary/80",
                ghost: "hover:bg-admin-secondary hover:text-admin-secondary-foreground text-admin-primary",
                link: "text-admin-primary underline-offset-4 hover:underline text-admin-primary",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

interface CalendarDatePickerProps
    extends React.HTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof multiSelectVariants> {
    id?: string;
    className?: string;
    date: DateRange;
    closeOnSelect?: boolean;
    numberOfMonths?: 1 | 2;
    yearsRange?: number;
    onDateSelect: (range: { from: Date; to: Date }) => void;
}

export const CalendarDatePicker = React.forwardRef<
    HTMLButtonElement,
    CalendarDatePickerProps
>(
    (
        {
            id = "calendar-date-picker",
            className,
            date,
            closeOnSelect = false,
            numberOfMonths = 2,
            // yearsRange = 10,
            onDateSelect,
            variant,
            ...props
        },
        ref,
    ) => {
        const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
        const [selectedRange, setSelectedRange] = React.useState<string | null>(
            numberOfMonths === 2 ? "This Year" : "Today",
        );
        const [monthFrom, setMonthFrom] = React.useState<Date | undefined>(
            date?.from,
        );

        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const handleClose = () => setIsPopoverOpen(false);

        const handleTogglePopover = () => setIsPopoverOpen(prev => !prev);

        const selectDateRange = (from: Date, to: Date, range: string) => {
            const startDate = startOfDay(toDate(from, { timeZone }));
            const endDate =
                numberOfMonths === 2
                    ? endOfDay(toDate(to, { timeZone }))
                    : startDate;
            onDateSelect({ from: startDate, to: endDate });
            setSelectedRange(range);
            setMonthFrom(from);
            closeOnSelect && setIsPopoverOpen(false);
        };

        const handleDateSelect = (range: DateRange | undefined) => {
            if (range) {
                let from = startOfDay(toDate(range.from as Date, { timeZone }));
                let to = range.to
                    ? endOfDay(toDate(range.to, { timeZone }))
                    : from;
                if (numberOfMonths === 1) {
                    if (range.from !== date.from) {
                        to = from;
                    } else {
                        from = startOfDay(
                            toDate(range.to as Date, { timeZone }),
                        );
                    }
                }
                onDateSelect({ from, to });
                setMonthFrom(from);
            }
            setSelectedRange(null);
        };

        const today = new Date();

        const dateRanges = [
            { label: "Today", start: today, end: today },
            {
                label: "Last 3 Days",
                start: subDays(today, 2),
                end: today,
            },
            {
                label: "Last 7 Days",
                start: subDays(today, 6),
                end: today,
            },
            {
                label: "Last 30 Days",
                start: subDays(today, 29),
                end: today,
            },
            {
                label: "Last 90 Days",
                start: subDays(today, 89),
                end: today,
            },
            {
                label: "Last Year",
                start: subYears(today, 1),
                end: today,
            },
            {
                label: "All Time",
                start: new Date(2024, 0, 1),
                end: today,
            },
        ];

        const formatWithTz = (date: Date, fmt: string) =>
            formatInTimeZone(date, timeZone, fmt);

        return (
            <>
                <style>
                    {`
            .date-part {
              touch-action: none;
            }
          `}
                </style>
                <Popover
                    open={isPopoverOpen}
                    onOpenChange={setIsPopoverOpen}
                >
                    <PopoverTrigger asChild>
                        <Button
                            id="date"
                            ref={ref}
                            {...props}
                            className={cn(
                                "w-auto",
                                multiSelectVariants({ variant, className }),
                            )}
                            onClick={handleTogglePopover}
                            suppressHydrationWarning
                        >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            <span>
                                {date?.from ? (
                                    date.to ? (
                                        <>
                                            <span
                                                id={`firstDay-${id}`}
                                                className="date-part"
                                            >
                                                {formatWithTz(date.from, "dd")}
                                            </span>{" "}
                                            <span
                                                id={`firstMonth-${id}`}
                                                className="date-part"
                                            >
                                                {formatWithTz(date.from, "LLL")}
                                            </span>
                                            ,{" "}
                                            <span
                                                id={`firstYear-${id}`}
                                                className="date-part"
                                            >
                                                {formatWithTz(date.from, "y")}
                                            </span>
                                            {numberOfMonths === 2 && (
                                                <>
                                                    {" - "}
                                                    <span
                                                        id={`secondDay-${id}`}
                                                        className="date-part"
                                                    >
                                                        {formatWithTz(
                                                            date.to,
                                                            "dd",
                                                        )}
                                                    </span>{" "}
                                                    <span
                                                        id={`secondMonth-${id}`}
                                                        className="date-part"
                                                    >
                                                        {formatWithTz(
                                                            date.to,
                                                            "LLL",
                                                        )}
                                                    </span>
                                                    ,{" "}
                                                    <span
                                                        id={`secondYear-${id}`}
                                                        className="date-part"
                                                    >
                                                        {formatWithTz(
                                                            date.to,
                                                            "y",
                                                        )}
                                                    </span>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <span
                                                id="day"
                                                className="date-part"
                                            >
                                                {formatWithTz(date.from, "dd")}
                                            </span>{" "}
                                            <span
                                                id="month"
                                                className="date-part"
                                            >
                                                {formatWithTz(date.from, "LLL")}
                                            </span>
                                            ,{" "}
                                            <span
                                                id="year"
                                                className="date-part"
                                            >
                                                {formatWithTz(date.from, "y")}
                                            </span>
                                        </>
                                    )
                                ) : (
                                    <span>Pick a date</span>
                                )}
                            </span>
                        </Button>
                    </PopoverTrigger>
                    {isPopoverOpen && (
                        <PopoverContent
                            className="w-auto"
                            align="end"
                            avoidCollisions={false}
                            onInteractOutside={handleClose}
                            onEscapeKeyDown={handleClose}
                            style={{
                                maxHeight:
                                    "var(--radix-popover-content-available-height)",
                                overflowY: "auto",
                            }}
                        >
                            <div className="flex">
                                {numberOfMonths === 2 && (
                                    <div className="hidden flex-col gap-1 border-r border-admin-foreground/10 pr-4 text-left md:flex">
                                        {dateRanges.map(
                                            ({ label, start, end }) => (
                                                <Button
                                                    key={label}
                                                    variant="ghost"
                                                    size="sm"
                                                    className={cn(
                                                        "justify-start hover:bg-admin-primary/90 hover:text-admin-background",
                                                        selectedRange ===
                                                            label &&
                                                            "bg-admin-primary text-admin-background hover:bg-admin-primary/90 hover:text-admin-background",
                                                    )}
                                                    onClick={() => {
                                                        selectDateRange(
                                                            start,
                                                            end,
                                                            label,
                                                        );
                                                        setMonthFrom(start);
                                                    }}
                                                >
                                                    {label}
                                                </Button>
                                            ),
                                        )}
                                    </div>
                                )}
                                <div className="flex flex-col">
                                    <div className="flex">
                                        <Calendar
                                            mode="range"
                                            defaultMonth={monthFrom}
                                            month={monthFrom}
                                            onMonthChange={setMonthFrom}
                                            selected={date}
                                            onSelect={handleDateSelect}
                                            numberOfMonths={numberOfMonths}
                                            showOutsideDays={false}
                                            className={className}
                                        />
                                    </div>
                                </div>
                            </div>
                        </PopoverContent>
                    )}
                </Popover>
            </>
        );
    },
);

CalendarDatePicker.displayName = "CalendarDatePicker";
